import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Tag, Percent, Euro, Calendar, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyPromo = {
  code: '',
  discount_type: 'percent',
  discount_value: '',
  min_order_amount: '',
  max_uses: '',
  expires_at: '',
  active: true,
  description: '',
};

const AdminPromoCodes = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(emptyPromo);

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/promo-codes`);
      setPromos(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromos(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyPromo);
    setShowModal(true);
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: String(promo.discount_value),
      min_order_amount: promo.min_order_amount ? String(promo.min_order_amount) : '',
      max_uses: promo.max_uses != null ? String(promo.max_uses) : '',
      expires_at: promo.expires_at || '',
      active: promo.active,
      description: promo.description || '',
    });
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.code || form.code.length < 3) {
      toast.error('Le code doit faire au moins 3 caractères');
      return;
    }
    const value = parseFloat(form.discount_value);
    if (!value || value <= 0) {
      toast.error('Saisis une valeur de réduction valide');
      return;
    }
    if (form.discount_type === 'percent' && value > 100) {
      toast.error('Le pourcentage ne peut pas dépasser 100');
      return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: value,
      min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      expires_at: form.expires_at || null,
      active: form.active,
      description: form.description || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/promo-codes/${editing.id}`, payload);
        toast.success('Code promo mis à jour');
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload);
        toast.success('Code promo créé');
      }
      setShowModal(false);
      fetchPromos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.id}`, { active: !promo.active });
      fetchPromos();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const remove = async (promo) => {
    if (!window.confirm(`Supprimer le code "${promo.code}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promo.id}`);
      toast.success('Code promo supprimé');
      fetchPromos();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const formatValue = (p) =>
    p.discount_type === 'percent'
      ? `${p.discount_value}%`
      : `${Number(p.discount_value).toFixed(2)} €`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1C1917]">Codes promo</h3>
          <p className="text-sm text-[#57534E]">Crée des codes de réduction pour tes clients</p>
        </div>
        <Button onClick={openCreate} className="bg-[#C62E2E] hover:bg-[#a02424] rounded-full" data-testid="create-promo-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau code
        </Button>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-[#57534E]">Chargement…</Card>
      ) : promos.length === 0 ? (
        <Card className="p-12 text-center">
          <Tag className="w-12 h-12 mx-auto mb-3 text-[#57534E] opacity-50" />
          <p className="text-[#57534E]">Aucun code promo pour le moment</p>
          <Button onClick={openCreate} className="mt-4 bg-[#C62E2E] hover:bg-[#a02424] rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Créer le premier
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {promos.map((p) => (
            <Card key={p.id} className={`p-4 ${!p.active ? 'opacity-60' : ''}`} data-testid={`promo-card-${p.code}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono font-bold text-lg text-[#C62E2E]">{p.code}</span>
                    {p.active ? (
                      <Badge className="bg-green-100 text-green-800 border border-green-300">Actif</Badge>
                    ) : (
                      <Badge className="bg-stone-100 text-stone-600">Désactivé</Badge>
                    )}
                  </div>
                  {p.description && <p className="text-xs text-[#78716C] mb-2">{p.description}</p>}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#57534E]">
                    <span className="flex items-center gap-1">
                      {p.discount_type === 'percent' ? <Percent className="w-3 h-3" /> : <Euro className="w-3 h-3" />}
                      <strong>{formatValue(p)}</strong>
                    </span>
                    {p.min_order_amount > 0 && (
                      <span className="flex items-center gap-1">
                        <Euro className="w-3 h-3" />
                        Min: {p.min_order_amount.toFixed(2)} €
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {p.current_uses}{p.max_uses != null ? `/${p.max_uses}` : ''} utilisé{p.current_uses > 1 ? 's' : ''}
                    </span>
                    {p.expires_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {p.expires_at}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Switch
                    checked={p.active}
                    onCheckedChange={() => toggleActive(p)}
                    data-testid={`promo-toggle-${p.code}`}
                  />
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="h-8 w-8" data-testid={`promo-edit-${p.code}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid={`promo-delete-${p.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le code' : 'Nouveau code promo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code *</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME10"
                className="font-mono uppercase"
                data-testid="promo-code-input"
              />
              <p className="text-xs text-[#78716C] mt-1">Sera converti automatiquement en majuscules</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">% Pourcentage</SelectItem>
                    <SelectItem value="fixed">€ Montant fixe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valeur *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  placeholder={form.discount_type === 'percent' ? '10' : '5.00'}
                  data-testid="promo-value-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Commande min. (€)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.min_order_amount}
                  onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Nb max d'utilisations</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Illimité"
                />
              </div>
            </div>
            <div>
              <Label>Expire le (optionnel)</Label>
              <Input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>
            <div>
              <Label>Description (admin)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Code de bienvenue, fête des mères…"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Code actif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-[#C62E2E] hover:bg-[#a02424]" data-testid="promo-save-btn">
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromoCodes;
